import { useSuspenseQueries } from '@tanstack/react-query';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenOrgJoin, NavigationScreenState } from '@gitbook/app-navigation';
import {
    Button,
    PageSplit,
    PageSplitBody,
    PageSplitCloseButton,
    PageSplitDescription,
    PageSplitMain,
    PageSplitTitle,
    toast,
} from '@gitbook/spine-react';

import { APIErrorBoundary, OrganizationIcon } from '../../Components';
import { NavigationRedirect, useNavigation } from '../../navigation';
import { apiQuery, useJoinOrganizationWithInvite } from '../../state';

/**
 * Screen when the user is following an invite for an organization.
 */
export function JoinOrganizationScreen(props: {
    screen: NavigationScreenOrgJoin;
}): React.ReactElement {
    const { screen } = props;

    return (
        <PageSplit>
            <PageSplitMain>
                <PageSplitCloseButton label="Close" href={{ screen: { type: 'home' } }} />
                <PageSplitBody size="small">
                    <APIErrorBoundary fallback={{ 404: <InvitationNotFound /> }}>
                        <LinkInviteScreenWrapper screen={screen} />
                    </APIErrorBoundary>
                </PageSplitBody>
            </PageSplitMain>
        </PageSplit>
    );
}

function LinkInviteScreenWrapper({ screen }: { screen: NavigationScreenOrgJoin }) {
    const [{ data: organization }, { data: invite }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId: screen.organization }),
            apiQuery('getOrganizationInviteLink', {
                organizationId: screen.organization,
                inviteId: screen.invite,
            }),
        ],
    });

    return invite && organization.inviteLinks ? (
        <LinkInviteScreenInner organization={organization} invite={invite} />
    ) : (
        <InvitationNotFound />
    );
}

/**
 * Component to render the given invite.
 */
function LinkInviteScreenInner({
    invite,
    organization,
}: {
    invite: api.OrganizationInviteLink;
    organization: api.Organization;
}): React.ReactElement {
    const { navigate } = useNavigation();
    const joinOrganizationWithInvite = useJoinOrganizationWithInvite();

    // Redirect to the organization if the membership exists and the user has
    // the necessary content permissions.
    if (invite.redundant) {
        return (
            <NavigationRedirect to={{ screen: getAfterAcceptedScreen(organization.id, invite) }} />
        );
    }

    // Create the membership and any content perms when the user accepts the invite
    const onPressJoin = async () => {
        await toast.catch(async () => {
            await joinOrganizationWithInvite.mutateAsync({
                orgId: organization.id,
                inviteId: invite.id,
            });
            toast.success(`You have joined "${organization.title}"`);
            navigate({
                screen: getAfterAcceptedScreen(organization.id, invite),
            });
        }, 'Error while joining organization');
    };

    return (
        <>
            <PageSplitTitle logo={<OrganizationIcon size={48} organization={organization} />}>
                Join {organization.title}
            </PageSplitTitle>
            <PageSplitDescription>
                <span data-testid="joinOrganization.screen">
                    You have been invited to join the <strong>{organization.title}</strong>{' '}
                    organization.
                </span>
            </PageSplitDescription>
            <Button
                kind="primary"
                onPress={onPressJoin}
                loadable
                label="Join the organization"
                size="large"
            />
        </>
    );
}

function InvitationNotFound() {
    const { navigate } = useNavigation();
    return (
        <>
            <PageSplitTitle>Invitation not found</PageSplitTitle>
            <PageSplitDescription>
                <span data-testid="joinOrganization.screen">
                    This invite link may be invalid or have been revoked.
                </span>
            </PageSplitDescription>
            <Button
                label="Close"
                size="large"
                kind="secondary"
                onPress={() => {
                    navigate({
                        screen: {
                            type: 'home',
                        },
                    });
                }}
            />
        </>
    );
}
/**
 * Returns the appropriate screen once the invite has been accepted.
 */
function getAfterAcceptedScreen(
    organization: string,
    invite: api.OrganizationInviteLink
): NavigationScreenState {
    if (isSpaceInvite(invite)) {
        return {
            type: 'space',
            space: invite.space.id,
            page: '/',
            organization,
        };
    }

    if (isCollectionInvite(invite)) {
        return {
            type: 'home',
            organization,
        };
    }

    return {
        type: 'home',
        organization,
    };
}

function isSpaceInvite(invite: api.OrganizationInviteLink): invite is api.InviteLinkToSpace {
    return 'space' in invite;
}

function isCollectionInvite(
    invite: api.OrganizationInviteLink
): invite is api.InviteLinkToCollection {
    return 'collection' in invite;
}
